import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, Wallet, User, Clock, LogOut } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import API from "@/utils/api";
import Logo from "@/components/ui/Logo";

export default function RiderLayout({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [rider, setRider] = useState(null);
    const locationInterval = useRef(null);

    useEffect(() => {
        const token = localStorage.getItem("rider_token");
        const riderData = localStorage.getItem("rider_data");
        if (!token || !riderData) {
            navigate("/rider/login");
            return;
        }

        try {
            setRider(JSON.parse(riderData));
        } catch (e) {
            navigate("/rider/login");
        }
    }, [navigate]);

    // Live Tracking
    useEffect(() => {
        if (!rider) return;

        const sendLocation = async () => {
            if (!("geolocation" in navigator)) return;

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    try {
                        const token = localStorage.getItem("rider_token");
                        await axios.post(`${API}/rider/location`, {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude
                        }, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                    } catch (error) {
                        console.error("Error sending location", error);
                    }
                },
                (error) => {
                    console.error("Geolocation error:", error);
                },
                { enableHighAccuracy: true }
            );
        };

        // Send immediately, then every 10s
        sendLocation();
        locationInterval.current = setInterval(sendLocation, 10000);

        return () => clearInterval(locationInterval.current);
    }, [rider]);

    const handleLogout = () => {
        localStorage.removeItem("rider_token");
        localStorage.removeItem("rider_data");
        navigate("/rider/login");
        toast.info("Sesión cerrada");
    };

    if (!rider) return null; // loading state essentially

    const navItems = [
        { path: "/rider/dashboard", label: "Activos", icon: <LayoutDashboard size={20} /> },
        { path: "/rider/jobs", label: "Disponibles", icon: <ShoppingBag size={20} /> },
        { path: "/rider/wallet", label: "Cartera", icon: <Wallet size={20} /> },
        { path: "/rider/history", label: "Historial", icon: <Clock size={20} /> },
        { path: "/rider/profile", label: "Perfil", icon: <User size={20} /> }
    ];

    return (
        <div className="min-h-screen bg-black text-white font-sans flex flex-col sm:max-w-md sm:mx-auto sm:border-x sm:border-white/10 relative overflow-hidden pb-24">
            {/* Ambient Background */}
            <div className="absolute top-0 w-full h-48 bg-gradient-to-b from-primary/20 to-transparent pointer-events-none -z-10" />

            {/* Header */}
            <header className="p-4 flex justify-between items-center border-b border-white/5 bg-white/5 backdrop-blur-md sticky top-0 z-50">
                <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden shadow-[0_0_15px_rgba(59,130,246,0.3)] backdrop-blur-md">
                        <Logo size={20} variant="icon" />
                    </div>
                    <span className="font-extrabold tracking-tight text-white italic">CONEXO</span>
                </div>
                <button onClick={handleLogout} className="p-2 text-slate-400 hover:text-white transition-colors">
                    <LogOut size={20} />
                </button>
            </header>

            {/* Main Content */}
            <main className="flex-1 overflow-y-auto">
                {children}
            </main>

            {/* Bottom Nav */}
            <nav className="fixed bottom-3 left-3 right-3 sm:max-w-md sm:mx-auto bg-black/80 border border-white/10 flex justify-around p-3 z-[100] backdrop-blur-2xl rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.8)]">
                {navItems.map((item) => {
                    const isActive = location.pathname.startsWith(item.path);
                    return (
                        <Link key={item.path} to={item.path} className={`flex flex-col items-center gap-1.5 p-1 transition-all duration-300 ${isActive ? "text-primary scale-105" : "text-slate-400 opacity-60 hover:opacity-100"}`}>
                            <div className={`p-2.5 rounded-xl transition-all duration-300 ${isActive ? "bg-primary/20 shadow-[0_0_20px_rgba(59,130,246,0.3)]" : ""}`}>
                                {React.cloneElement(item.icon, { size: 24 })}
                            </div>
                            <span className={`text-[11px] uppercase font-bold tracking-tight transition-all ${isActive ? "opacity-100" : "opacity-70"}`}>{item.label}</span>
                        </Link>
                    )
                })}
            </nav>
        </div>
    );
}
